import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { OnboardingPageService } from '../../onboarding-page/onboarding-page.service';
import { ToastMessage } from '../../toast-message/toast-message.service';


@Component({
  selector: 'app-fleet-manager-onboarding-details',
  template: require('./fleet-manager-onboarding-details.component.html'),
  providers: [OnboardingPageService]
})
export class FleetManagerOnboardingDetailsComponent implements OnInit {
  overHeadPanelTitle = 'Fleet Manager Onboarding - Details';
  activeSection: 'info' | 'profile' = 'info';

  // Basic info fields (no APIs wired yet)
  name: string = '';
  gstNumber: string = '';
  emailsText: string = '';
  cityId: string = '';
  fleetId: string = '';

  // Static options (to be fetched later)
  citiesList: Array<any> = [];
  fleetsList: Array<{ _id: string, name: string, mobile?: string }> = [];
  fleetType: string = 'third_party';
  fleetTypeOptions: string[] = ['third_party', 'rental'];
  userId: string = '';
  profilePicBase64: string = '';
  profileRotateDeg: number = 0;
  profileUploadLoading: boolean = false;
  infoApiSuccess: boolean = false;

  constructor(private router: Router, private route: ActivatedRoute, private onboardingService: OnboardingPageService, private toast: ToastMessage) {
    const params = this.route.snapshot.queryParamMap;
    const mobile = params.get('mobile');
    const qpName = params.get('name') || '';
    const qpCityId = params.get('cityId') || '';
    const qpFleetId = params.get('fleetId') || '';
    const qpFleetType = params.get('fleetType') || '';
    const qpUserId = params.get('userId') || '';
    const qpProfilePicLink = params.get('profilePicLink') || '';
    if (qpName) { this.name = qpName; }
    if (qpCityId) { this.cityId = qpCityId; }
    if (qpFleetId) { this.fleetId = qpFleetId; }
    if (qpFleetType) { this.fleetType = qpFleetType; }
    if (qpUserId) { this.userId = qpUserId; }
    if (qpProfilePicLink) {
      this.tryLoadProfileFromStatic(qpProfilePicLink);
    }
    this.infoApiSuccess = !!(this.userId || (this.name && this.cityId && this.fleetId));
  }

  ngOnInit() {
    this.loadCities();
    this.loadFleets();
  }

  loadCities() {
    this.onboardingService.getCitiesList().subscribe(
      (resp: any) => {
        this.citiesList = resp && resp.geolayers ? resp.geolayers : [];
      },
      _err => {}
    );
  }

  loadFleets() {
    const currentUserId = localStorage.getItem('userId') || '';
    // Map UI value to backend expected value
    const uiType = this.fleetType || 'third_party';
    const backendType = uiType === 'third_party' ? 'thirdParty' : uiType;
    this.onboardingService.getFleetsByType(backendType, currentUserId).subscribe(
      (resp: any) => {
        const data = resp && resp.data ? resp.data : [];
        this.fleetsList = Array.isArray(data)
          ? data.map((f: any) => ({ _id: f._id, name: f.name || f._id, mobile: (f.mobile || f.mobileNumber || '') }))
          : [];
      },
      _err => {}
    );
  }

  onFleetTypeChange() {
    this.fleetId = '';
    this.loadFleets();
  }

  isFormValid() {
    return !!(this.name && this.cityId && this.fleetId && this.fleetType);
  }

  goBack() {
    this.router.navigate(['/fleetManagerOnboarding']);
  }

  switchSection(section: 'info' | 'profile') {
    this.activeSection = section;
  }

  async upload() {
    const mobile = this.route.snapshot.queryParamMap.get('mobile') || '';
    const actorCityId = this.cityId; // using selected city
    const name = this.name;
    try {
      // API1: check if user exists by mobile
      let isPresent = false;
      let existingUserId = '';
      let existingRoles = [];
      try {
        const resp: any = await this.onboardingService.getUserByMobileViaBff(mobile).toPromise();
        isPresent = !!(resp && resp.info && String(resp.info.status).toLowerCase() === 'success' && resp.data && resp.data._id);
        existingUserId = isPresent ? resp.data._id : '';
        existingRoles = isPresent && Array.isArray(resp.data.roles) ? resp.data.roles : [];
      } catch (_err) {
        // Treat any error (e.g., 404 Not Found) as user not present
        isPresent = false;
      }

      if (isPresent && existingUserId) {
        const roles = Array.from(new Set([...existingRoles, 'fleetManager']));
        await this.onboardingService.updateUserRolesViaBff(existingUserId, roles).toPromise();
        this.userId = existingUserId;
      } else {
        // API3: create
        const createResp: any = await this.onboardingService.createUserViaBff({ name, city: actorCityId, mobile }).toPromise();
        const createdUserId = createResp && createResp.user && createResp.user._id ? createResp.user._id : '';
        this.userId = createdUserId;
      }
      // create fleet manager entry in captain-service
      if (this.userId) {
        await this.onboardingService.createFleetManager({
          source: 'supply-dashboard',
          mobile,
          cityId: this.cityId,
          userId: this.userId,
          mobileVerified: true,
          fleetId: this.fleetId,
          name: this.name,
          status: 'active'
        }).toPromise();
        this.toast.openSnackBarSuccess('Fleet Manager created/updated');
        this.infoApiSuccess = true;
      }
    } catch (e) {
      this.toast.openSnackBarError(e, 4000);
    }
  }

  onProfilePicFile(event: any) {
    const files = event && event.target && event.target.files ? event.target.files : null;
    const file = files && files.length ? files[0] : null;
    if (!file) { return; }
    const reader = new FileReader();
    reader.onload = () => {
      this.profilePicBase64 = String(reader.result || '');
      this.profileRotateDeg = 0;
    };
    reader.readAsDataURL(file);
  }

  get showInfoTick(): boolean {
    return !!(this.infoApiSuccess && this.isFormValid());
  }

  get showProfileTick(): boolean {
    return !!this.profilePicBase64;
  }

  async uploadProfilePic() {
    if (!this.profilePicBase64) { return; }
    try {
      this.profileUploadLoading = true;
      await this.onboardingService.uploadFleetManagerProfile(this.profilePicBase64, this.userId).toPromise();
      this.toast.openSnackBarSuccess('Profile photo uploaded');
    } catch (e) {
      this.toast.openSnackBarError('Failed to upload profile photo', 3000);
    } finally {
      this.profileUploadLoading = false;
    }
  }

  rotateProfile(direction: 'left' | 'right') {
    this.profileRotateDeg += direction === 'left' ? -90 : 90;
  }

  private tryLoadProfileFromStatic(link: string) {
    try {
      const name = decodeURIComponent((link || '').split('/').pop() || '');
      if (!name) {
        this.profilePicBase64 = link;
        return;
      }
      // Use captain-onboarding bucket for profile images in staging
      this.onboardingService.getStaticFileUrl('captain-onboarding-document-profile', 'image', name)
        .subscribe((res: any) => {
          if (res && res.url) {
            this.profilePicBase64 = res.url;
          } else {
            this.profilePicBase64 = link;
          }
        }, _e => {
          this.profilePicBase64 = link;
        });
    } catch (_e) {
      this.profilePicBase64 = link;
    }
  }
}


